import { format, getDay, parse, startOfWeek } from "date-fns";
import enUS from "date-fns/locale/en-US";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import React from "react";
import date from "./dateAdapter.js";
import * as utils from "./utils.jsx";
import { WorkParty } from "./workparty.jsx";

import { useBackendData } from "./hooks.ts";

var calendarLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { "en-US": enUS },
});

function workPartyStart(workParty) {
  return date.toDate(workParty.start_date_time);
}

function workPartyEnd(workParty) {
  return date.toDate(workParty.end_date_time);
}

function BooleanFacet({ name, label, value, updateQuery }) {
  function handleChange() {
    var filters = {};
    filters[name] = value == "false" ? "true" : "false";
    updateQuery(filters);
  }

  return (
    <utils.FormField
      type="checkbox"
      name={name}
      label={label}
      checked={value == "true"}
      onChange={handleChange}
    />
  );
}

function MultiChoiceFacet({
  name,
  label = "",
  options,
  value,
  updateQuery,
  showAll,
  icons = {},
}) {
  const checked = React.useMemo(() => {
    const checked = {};
    if (value !== undefined) {
      (Array.isArray(value) ? value : [value]).forEach((item) => {
        checked[item] = 1;
      });
    }
    return checked;
  }, [value]);

  function handleChange(event) {
    let newChecked = { ...checked };
    var optionvalue = event.target.value;
    if (optionvalue) {
      if (newChecked[optionvalue] === undefined) {
        newChecked[optionvalue] = 1;
      } else {
        delete newChecked[optionvalue];
      }
    } else {
      newChecked = {};
    }
    updateQuery({ [name]: Object.keys(newChecked) });
  }

  const allSelected = !Object.keys(checked).length;
  return (
    <fieldset>
      {showAll ? (
        <utils.FormField
          type="checkbox"
          name={name}
          value=""
          label={"All " + label}
          checked={allSelected}
          onChange={handleChange}
        />
      ) : null}
      <div className={"disabled on"}>
        {options.map((o) => {
          var optionvalue = o.value || o;
          var label = o.label || o;
          var labelclass = "";
          if (o.hasOwnProperty("count") && o.count == 0) {
            labelclass = "empty";
          }
          return (
            <utils.FormField
              type="checkbox"
              name={name}
              value={optionvalue}
              label={label}
              icon={icons[optionvalue] || false}
              checked={Boolean(checked[optionvalue])}
              onChange={handleChange}
              key={optionvalue}
              label_class={labelclass}
            />
          );
        })}
      </div>
    </fieldset>
  );
}

function TextFacet({ name, initialValue, placeholder, updateQuery }) {
  const [value, setValue] = React.useState(initialValue);
  React.useEffect(() => setValue(initialValue), [initialValue]);

  function handleBlur() {
    updateQuery({ [name]: value });
  }

  function handleKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      handleBlur();
    }
  }

  return (
    <input
      type="text"
      name={name}
      placeholder={placeholder}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      value={value || ""}
    />
  );
}

var DATE_RANGES = [
  ["In the future", "In the future"],
  ["This Week", "This Week"],
  ["Next Week", "Next Week"],
  ["This Month", "This Month"],
  ["Next Month", "Next Month"],
  ["Next Six Weeks", "Next Six Weeks"],
  ["This Year", "This Year"],
  ["Custom", "Custom Dates"],
];

function DateRangeFacet({
  name,
  startName,
  endName,
  value,
  label,
  updateQuery,
  dateRanges = DATE_RANGES,
}) {
  function handleChange(field, changedValue) {
    const newValue = { ...value };
    if (changedValue.target) {
      changedValue = changedValue.target.value;
    }
    newValue[field] = changedValue;

    // clean up
    if (newValue[name] !== "Custom") {
      newValue[startName] = undefined;
      newValue[endName] = undefined;
      newValue.includeCompleted = false;
    } else {
      // if custom search and start date in the past, include closed wps
      const startDate = date.toDate(newValue[startName]);
      if (!startDate || startDate < date.now()) {
        newValue.includeClosed = true;
        newValue.includeCompleted = true;
      } else {
        newValue.includeCompleted = false;
      }
    }

    updateQuery(newValue);
  }

  return (
    <fieldset>
      <h4>{label}</h4>
      <utils.FormField
        type="select"
        name={name}
        value={value[name]}
        onChange={handleChange.bind(null, name)}
      >
        {dateRanges.map((o) => (
          <option value={o[0]} key={o[0]}>
            {o[1]}
          </option>
        ))}
      </utils.FormField>
      {value[name] == "Custom" ? (
        <div className="row">
          <br />
          <div className="col">
            <label>Start</label>
            <input
              type="date"
              value={value[startName]}
              onChange={handleChange.bind(null, startName)}
            />
          </div>
          <div className="col">
            <label>End</label>
            <input
              type="date"
              value={value[endName]}
              onChange={handleChange.bind(null, endName)}
            />
          </div>
        </div>
      ) : (
        ""
      )}
    </fieldset>
  );
}

var ROSTER_REQUESTS = [
  "ACLs Needed",
  {
    value: "Extra Volunteers Needed",
    label: "Extra Crew Members Needed",
  },
  "Sawyers Needed",
];

var ROSTER_ICONS = {
  "ACLs Needed": window.base_url + "static/svg/helmet-orange.svg",
  "Extra Volunteers Needed": window.base_url + "static/svg/helmet-green.svg",
  "Sawyers Needed": window.base_url + "static/images/sawyers-needed.png",
};

var WEEKDAY_OPTIONS = ["Weekends", "Weekdays"];

function WorkPartyFilters({
  filters,
  show_roster_requests,
  aggregations,
  updateQuery,
}) {
  function getInitialState() {
    var probably_mobile = window.outerWidth < 960;
    return {
      formCollapsed: probably_mobile ? true : false,
      advCollapsed: true,
    };
  }

  const [state, setState] = React.useState(getInitialState);

  function toggleForm() {
    setState((currentState) => ({
      ...currentState,
      formCollapsed: !currentState.formCollapsed,
    }));
    return false;
  }

  function toggleAdvanced(e) {
    e.preventDefault();
    setState((currentState) => ({
      ...currentState,
      advCollapsed: !currentState.advCollapsed,
    }));
  }

  var roster_requests;
  if (show_roster_requests) {
    roster_requests = (
      <div>
        <hr />
        <MultiChoiceFacet
          name="roster_requests"
          label="Roster Requests"
          options={ROSTER_REQUESTS}
          value={filters.roster_requests}
          icons={ROSTER_ICONS}
          showAll={false}
          updateQuery={updateQuery}
        />
      </div>
    );
  }
  return (
    <div className="module form subtle">
      <div className="ui panel" style={{ zIndex: 2 }}>
        <span
          className={
            "icon-plus-circled js-toggle" + (state.formCollapsed ? "" : " on")
          }
          onClick={toggleForm}
        ></span>
      </div>
      <div className="container" id="search_filters">
        <header>
          <h2>Search Work Parties</h2>
        </header>
        <form>
          <DateRangeFacet
            name="date"
            startName="start"
            endName="end"
            label="When"
            value={filters}
            updateQuery={updateQuery}
          />
          <div className="day_filter">
            <MultiChoiceFacet
              name="days"
              options={WEEKDAY_OPTIONS}
              showAll={false}
              value={filters.days}
              updateQuery={updateQuery}
            />
          </div>
          <hr />
          <div className={"toggler" + (state.formCollapsed ? "" : " on")}>
            <MultiChoiceFacet
              name="types"
              label="Work Party Types"
              options={aggregations.types.buckets.map((t) => ({
                value: t.key,
                label: t.key,
                count: t.doc_count,
              }))}
              showAll={true}
              value={filters.types}
              updateQuery={updateQuery}
            />
            <hr />
            <MultiChoiceFacet
              name="regions"
              label="Regions"
              options={aggregations.regions.buckets.map((t) => ({
                value: t.key,
                label: t.key,
                count: t.doc_count,
              }))}
              showAll={true}
              value={filters.regions}
              updateQuery={updateQuery}
            />
            <hr />

            <h5>
              <a
                href="#"
                className={"js-toggle " + (state.advCollapsed ? "" : " on")}
                onClick={toggleAdvanced}
              >
                Advanced Options
              </a>
            </h5>

            <div className={"toggler " + (state.advCollapsed ? "" : " on")}>
              <hr />
              <fieldset>
                <BooleanFacet
                  name="includeWaitlisted"
                  label="Include work parties with waitlists"
                  value={
                    filters.includeWaitlisted == "false" ? "false" : "true"
                  }
                  updateQuery={updateQuery}
                />
                <BooleanFacet
                  name="includeClosed"
                  label="Include work parties with closed registration"
                  value={filters.includeClosed == "true" ? "true" : "false"}
                  updateQuery={updateQuery}
                />
                <hr />
                <TextFacet
                  name="text"
                  placeholder="keywords"
                  initialValue={filters.text}
                  updateQuery={updateQuery}
                />
                <hr />
                <MultiChoiceFacet
                  name="features"
                  label="Features"
                  options={aggregations.features.buckets.map((t) => ({
                    value: t.key,
                    label: t.key,
                    count: t.doc_count,
                  }))}
                  showAll={true}
                  value={filters.features}
                  updateQuery={updateQuery}
                />
                {roster_requests}
              </fieldset>
            </div>
            <hr />
          </div>
        </form>
      </div>
    </div>
  );
}

function WorkPartyMap({
  center = [-120.91742004345062, 47.334836573775306],
  mapbox_token,
  results,
  zoom = 5,
}) {
  const mapboxgl = window.mapboxgl;
  const containerRef = React.useRef(null);
  const loadedRef = React.useRef(false);
  const mapRef = React.useRef(null);
  const markersRef = React.useRef([]);
  const markersByPositionRef = React.useRef({});
  const addCurrentResults = React.useEffectEvent(() => {
    results.forEach(addMarker);
  });

  React.useEffect(() => {
    mapboxgl.accessToken = mapbox_token;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/wta/cjim5y67p53382snwau2f13wx",
      center,
      maxBounds: [
        [-126.4291758433375, 44.95995524063895],
        [-115.40566424356358, 49.6454385564096],
      ],
      zoom,
      minZoom: 5,
    });
    mapRef.current = map;
    map.on("load", () => {
      loadedRef.current = true;
      map.addControl(new mapboxgl.NavigationControl());
      addCurrentResults();
    });
    return () => {
      clearMarkers();
      map.remove();
      mapRef.current = null;
      loadedRef.current = false;
    };
  }, [mapbox_token]);

  React.useEffect(() => {
    if (!loadedRef.current) return;
    clearMarkers();
    results.forEach(addMarker);
    mapRef.current.jumpTo({
      center,
      zoom,
    });
  }, [results, center, zoom]);

  function addMarker(wp) {
    var location;
    if (wp.trailhead) {
      location = wp.trailhead.location;
    } else {
      var segment = utils.wpPrimarySegment(wp);
      if (segment === null) return;
      location = segment.start_point;
    }
    if (location) {
      var map = mapRef.current;
      var position = [location.longitude, location.latitude];
      var existing = markersByPositionRef.current[position.toString()];
      if (existing) {
        var parties = existing.parties;
        parties.push(wp);
        parties.sort(function (a, b) {
          return a.start_date_time > b.start_date_time
            ? 1
            : a.start_date_time == b.start_date_time
              ? 0
              : -1;
        });
        var wp_texts = parties.map(function (p) {
          var start = date.toDate(p.start_date_time);
          var end = date.toDate(p.end_date_time);
          var dates = date.format(start, "MMM D");
          if (
            date.startOfDay(start).getTime() !== date.startOfDay(end).getTime()
          ) {
            dates += " - " + date.format(end, "MMM D");
          }
          return (
            '<div class="header"><h4><a href="' +
            window.base_url +
            "workparty/" +
            p._id +
            '">' +
            p.name +
            "<span>" +
            dates +
            "</span></a></h4></div>"
          );
        });
        var popup =
          '<div class="infowindow-content">' +
          wp_texts.join("<br/>") +
          "</div>";
        existing.getPopup().setHTML(popup);
        return;
      }

      var start = date.toDate(wp.start_date_time);
      var end = date.toDate(wp.end_date_time);
      var dates = date.format(start, "MMM D");
      if (date.startOfDay(start).getTime() !== date.startOfDay(end).getTime()) {
        dates += " - " + date.format(end, "MMM D");
      }
      var popup =
        '<div class="infowindow-content"><div class="header"><h4>' +
        wp.name +
        "<span>" +
        dates +
        '</span></h4></div><div class="inner"><h5>' +
        wp.work_party_type.name +
        "</h5><div>" +
        wp.overview +
        '</div><p><a href="' +
        window.base_url +
        "workparty/" +
        wp._id +
        '">View</a></p></div></div>';

      var el = document.createElement("div");
      el.className = "marker";
      var marker = new mapboxgl.Marker(el);
      marker.setLngLat(position);
      marker.setPopup(
        new mapboxgl.Popup({
          offset: 25,
        }).setHTML(popup),
      );
      marker.parties = [wp];
      marker.addTo(map);
      markersByPositionRef.current[position.toString()] = marker;
      markersRef.current.push(marker);
    }
  }

  function clearMarkers() {
    var markers = markersRef.current;
    for (var i = 0; i < markers.length; i++) {
      markers[i].remove();
    }
    markersRef.current = [];
    markersByPositionRef.current = {};
  }

  return <div className="map" id="results-map" ref={containerRef}></div>;
}

function WorkPartyPagination({
  num = 0,
  size = 10,
  prev_url,
  next_url,
  urls,
  updateQuery,
}) {
  function handleClick(event) {
    event.preventDefault();
    var href = event.target.closest("a").getAttribute("href");
    var filters = utils.parseQueryString(new URL(href, location.href).search);
    updateQuery(filters);
    window.scrollTo({
      top:
        document.querySelector(".pagination").getBoundingClientRect().top +
        window.scrollY,
      behavior: "smooth",
    });
  }

  var prev = prev_url ? (
    <li>
      <a href={prev_url} onClick={handleClick}>
        Previous {size} items
      </a>
    </li>
  ) : (
    ""
  );
  var next = next_url ? (
    <li>
      <a href={next_url} onClick={handleClick}>
        Next {size} items
      </a>
    </li>
  ) : (
    ""
  );
  var pages = urls.map((url, index) => {
    return index == num ? (
      <li key={index}>
        <span className="pagenum">{index + 1}</span>
      </li>
    ) : (
      <li key={index}>
        <a href={url} onClick={handleClick}>
          {index + 1}
        </a>
      </li>
    );
  }, null);
  return (
    <div className="ui">
      <ul className="pagination" style={{ marginTop: 0 }}>
        {prev}
        {pages}
        {next}
      </ul>
    </div>
  );
}

function WorkPartyBanner() {
  const banner = useBackendData({
    url: window.cms_base_url + "vms-banner",
  });

  return banner ? (
    <div className="item ad">
      <div className="row">
        <div className="col one">&nbsp;</div>
        <div className="col ten">
          <h1>{banner.title}</h1>
          <h2 dangerouslySetInnerHTML={{ __html: banner.text }} />
        </div>
        <div className="col one">&nbsp;</div>
      </div>
      <div
        className="bg"
        style={{ backgroundImage: "url(" + banner.url + "/image)" }}
        title={banner.caption || ""}
      ></div>
    </div>
  ) : (
    <div />
  );
}

function WorkPartySearchLeadText() {
  const page = useBackendData({
    url: window.cms_base_url + "vms-page",
  });

  return page ? (
    <section className="module page-lead-text">
      <div dangerouslySetInnerHTML={{ __html: page.text }} />
    </section>
  ) : (
    <section />
  );
}

function WorkPartySearch({ user, mapbox_token }) {
  const baseUrl = `${window.base_url}workparties.json`;
  const initialUrl = `${baseUrl}${location.search}`;
  const qs = location.search.substring(1);
  const [url, setUrl] = React.useState(initialUrl);
  const [filters, setFilters] = React.useState(utils.parseQueryString(qs));
  const month = date.toDate(filters.month) || date.now();
  const tab = filters.tab || "list";
  const data = useBackendData({ url, resetWhileLoading: false });
  const [firstLoad, setFirstLoad] = React.useState(true);

  const handlePopState = React.useEffectEvent(onPopState);
  React.useEffect(() => {
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  function updateQuery(changedFilters) {
    var url = window.base_url + "workparties.json",
      loc;
    var newFilters = { ...filters };
    if (changedFilters.batch_num === undefined) {
      delete changedFilters.batch_num;
    }
    Object.keys(changedFilters).forEach(function (k) {
      var v = changedFilters[k];
      if (v === undefined) {
        delete newFilters[k];
      } else {
        newFilters[k] = v;
      }
    });
    if (newFilters) {
      var query = [];
      Object.keys(newFilters).forEach(function (k) {
        var v = newFilters[k];
        if (Array.isArray(v)) {
          v.forEach(function (v) {
            query.push(k + "=" + encodeURIComponent(v));
          });
        } else {
          query.push(k + "=" + encodeURIComponent(v));
        }
      });
      loc = "?" + query.join("&");
      url += loc;
    }
    setFilters(newFilters);
    setUrl(url);
    setFirstLoad(false);

    history.pushState(newFilters, "WP Search", loc);
  }

  function onPopState() {
    var qs = window.location.search.substring(1);
    const url = baseUrl + window.location.search;
    if (url != window.location.href) {
      setFilters(utils.parseQueryString(qs));
      setUrl(url);
    }
  }

  function setTab(tab, event) {
    event.preventDefault();
    updateQuery({ tab: tab });
  }

  function navigateCalendar(d) {
    updateQuery({ month: date.format(d, "YYYY-MM-DD") });
  }

  function navigateToEvent(event) {
    location.href = window.base_url + "workparty/" + event._id;
  }

  var isCL =
    user &&
    (user.role == "Crew Leader" ||
      user.role == "Admin" ||
      user.lead_ambassador);
  var isACL = user && (user.role == "Assistant Crew Leader" || isCL);
  var results = data?.results || null;
  var items = results ? results.items : [];
  var first_result = null;
  var banner = false;
  if (firstLoad && items.length) {
    first_result = items[0];
    items = items.slice(1);
    banner = true;
  }
  return (
    <div>
      <section className="module breadcrumb">
        <ul>
          <li>
            <a href={window.cms_base_url}>Home</a>
          </li>
          <li>
            <a href={window.cms_base_url + "/volunteer"}>Volunteer</a>
          </li>
          <li>Find a Work Party</li>
        </ul>
      </section>
      <h1>Find a Work Party</h1>
      <WorkPartySearchLeadText />
      <section className="layout secondary left">
        {data ? (
          <WorkPartyFilters
            aggregations={data.aggregations}
            show_roster_requests={data.show_roster_requests}
            filters={filters}
            updateQuery={updateQuery}
          />
        ) : (
          ""
        )}
      </section>
      <section className="layout primary right">
        <div className="ui tabs ajax">
          <ul>
            <li className={tab == "list" ? "on" : ""}>
              <a href="#" onClick={setTab.bind(null, "list")}>
                <span className="icon-th-list"></span>List
              </a>
            </li>
            <li className={tab == "map" ? "on" : ""}>
              <a href="#" onClick={setTab.bind(null, "map")}>
                <span className="icon-map-1"></span>Map
              </a>
            </li>
            <li className={tab == "calendar" ? "on" : ""}>
              <a href="#" onClick={setTab.bind(null, "calendar")}>
                <span className="icon-calendar-empty"></span>Calendar
              </a>
            </li>
          </ul>
        </div>
        <div className="js-tab-target">
          <section
            className="module highlight"
            style={{ minHeight: 0, marginBottom: "0 !important" }}
          >
            <div className="container" style={{ marginBottom: "0 !important" }}>
              {!data ? (
                <h3>Loading...</h3>
              ) : (
                <h3>
                  Found <span>{results.seqlen}</span> Work Part
                  {results.seqlen == 1 ? "y" : "ies"}
                </h3>
              )}
            </div>
          </section>
          {tab == "list" ? (
            <div className="js-tab-target" key="list">
              {results ? (
                <WorkPartyPagination
                  prev_url={results.prev_url}
                  next_url={results.next_url}
                  urls={results.batch_urls}
                  num={results.num}
                  updateQuery={updateQuery}
                />
              ) : null}
              <div className="module view list">
                {first_result ? (
                  <WorkParty
                    data={first_result}
                    user={user}
                    isACL={isACL}
                    key={first_result._id}
                  />
                ) : null}
                {first_result ? <WorkPartyBanner /> : null}
                {items.length
                  ? items.map(function (wp) {
                      return (
                        <WorkParty
                          data={wp}
                          user={user}
                          isACL={isACL}
                          key={wp._id}
                        />
                      );
                    })
                  : null}
              </div>
              {results ? (
                <WorkPartyPagination
                  prev_url={results.prev_url}
                  next_url={results.next_url}
                  urls={results.batch_urls}
                  num={results.num}
                  updateQuery={updateQuery}
                />
              ) : null}
            </div>
          ) : tab == "map" ? (
            <WorkPartyMap
              key="map"
              results={results ? results.all_items : []}
              mapbox_token={mapbox_token}
            />
          ) : (
            <Calendar
              key="calendar"
              localizer={calendarLocalizer}
              date={month}
              events={results ? results.all_items : []}
              views={["month"]}
              titleAccessor="name"
              startAccessor={workPartyStart}
              endAccessor={workPartyEnd}
              popup={true}
              onSelectEvent={navigateToEvent}
              onNavigate={navigateCalendar}
              timeslots={2}
            />
          )}
        </div>
      </section>
    </div>
  );
}

export {
  DateRangeFacet,
  MultiChoiceFacet,
  TextFacet,
  WorkPartyBanner,
  WorkPartyMap,
  WorkPartySearch,
  WorkPartySearchLeadText,
};
